import { debug as debugGen } from "debug";
import pageManager, { CachePage } from "../page";
import { ActionItem } from "../types";
import { ENUM_USER_ACTION_REPORT_TYPE } from "../actionTypes";

const debug = debugGen("vtTracker:Compose");

const VALID_VTNODE = ["appid", "pageid", "mdid", "sbmdid", "itemid"];

export interface PathNode {
    el: Element;
    attr: {
        appid?: string;
        pageid?: string;
        mdid?: string;
        sbmdid?: string;
        itemid?: string;
        itemtype?: string;
        itemrule?: string;
        position?: string;
        expose?: string;
        trace?: string;
        actionid?: string;
        touin?: string;
        busi?: string;
        reporttype?: string;
        uin?: string;
        trigger?: string;
    };
}

export enum ACTION_LEVEL {
    UNKNOWN = 0,
    PAGE = 1,
    MODULE = 2,
    SUBMODULE = 3,
    ITEM = 4
}

export default class EventCompose {
    protected event: Event;

    protected pageEl: Element;

    protected targetEl: Element;

    protected pathNodes: Array<PathNode> = [];

    public page: CachePage;

    constructor(evt: Event) {
        this.event = evt;

        this.pathNodes = this.path2App().reverse();

        this.pageEl = this.pathNodes[0].el;

        this.targetEl = this.pathNodes[this.pathNodes.length - 1].el;

        this.page = pageManager.getPage(this.pageEl);
    }

    compose(): ActionItem {
        return {
            appEl: null,
            appid: "",
            page_id: "",
            module_id: "",
            sub_module_id: "",
            action_id: 0,
            action_value: 0
        };
    }

    get target(): Element {
        return this.targetEl;
    }

    /**
     * 过滤出上报链路关心的节点
     * @param  {Event}       e [description]
     * @return {Array<PathNode>}   [description]
     */
    private filterTrackerNodes(e: Event): Array<PathNode> {
        const nodes: Array<EventTarget> =
            e["vpath"] || e["path"] || e.composedPath();

        const fnodes: Array<PathNode> = nodes.reduce(
            (arr: Array<PathNode>, it: EventTarget): Array<PathNode> => {
                if (it instanceof Element) {
                    const pn: PathNode = EventCompose.vtNodeInfo(it);

                    if (pn !== null) {
                        arr.push(pn);
                    }
                }

                return arr;
            },
            []
        );

        return fnodes;
    }

    static vtNodeInfo(el: Element): PathNode {
        if (!(el instanceof Element)) {
            return null;
        }

        const attrs: NamedNodeMap = el.attributes;

        const node: PathNode = {
            el,
            attr: {}
        };

        let keyAttrCount = 0;

        for (let i = 0, len = attrs.length; i < len; i += 1) {
            if (attrs[i].name?.startsWith("vt-")) {
                const attrName = attrs[i].name.substring(3);

                node.attr[attrName] = attrs[i].value;

                if (VALID_VTNODE.includes(attrName)) {
                    keyAttrCount += 1;
                }
            }
        }

        return Object.keys(node.attr).length > 0 && keyAttrCount > 0
            ? node
            : null;
    }

    private path2App(): Array<PathNode> {
        const ftnodes: Array<PathNode> = this.filterTrackerNodes(this.event);

        return EventCompose.fixNodePath(ftnodes);
    }

    static fixNodePath(ftnodes: Array<PathNode>): Array<PathNode> {
        const idx = ftnodes.findIndex((n: PathNode) => {
            return typeof n.attr.appid === "string";
        });

        if (idx === -1) {
            return ftnodes.concat({
                el: document.documentElement,
                attr: {}
            });
        }

        const nodes: Array<PathNode> = ftnodes.slice(0, idx + 1);

        // 补齐省略的子模块
        if (
            typeof nodes[0].attr.itemid === "string" &&
            nodes.findIndex(n => typeof n.attr.sbmdid === "string") < 0
        ) {
            nodes.splice(1, 0, {
                el: null,
                attr: {}
            });
        }

        return nodes;
    }

    protected extraActionItem(): {
        level: ACTION_LEVEL;
        page: CachePage;
        item: ActionItem;
    } {
        return EventCompose.pathNodeAction(this.pathNodes);
    }

    /**
     * 合并busi_info，后面的优先级更高
     * @param  {[type]} busiInfo1 [description]
     * @param  {[type]} busiInfo2 [description]
     * @return {string}           [description]
     */
    static mergeBusiInfo(busiInfo1, busiInfo2): string {
        let busiInfo = busiInfo1 || "";

        if (busiInfo2) {
            // 如果page和item上都有
            // 这种情况下要求两者都是标准的json
            if (busiInfo) {
                try {
                    const pbusi = JSON.parse(busiInfo);
                    const ibusi = JSON.parse(busiInfo2);

                    busiInfo = JSON.stringify({ ...pbusi, ...ibusi });
                } catch (err) {
                    busiInfo = busiInfo2;
                }
            } else {
                busiInfo = busiInfo2;
            }
        }

        return busiInfo;
    }

    static pathNodeAction(
        nodes: Array<PathNode>
    ): { level: ACTION_LEVEL; page: CachePage; item: ActionItem } {
        const item: ActionItem = {
            appEl: null,
            appid: "",
            page_id: "",
            module_id: "",
            sub_module_id: "",
            item_id: "",
            action_id: 0,
            action_value: 0,
            action_attr: 0,
            reportType: ENUM_USER_ACTION_REPORT_TYPE.RECOM_EFEECT_REPORT
        };

        const nd: PathNode = nodes[0];
        const page: CachePage = pageManager.getPage(nd.el);

        let level: ACTION_LEVEL = ACTION_LEVEL.UNKNOWN;

        item.appEl = nd.el;
        item.appid = nd.attr.appid || page.appid;
        item.page_id = nd.attr.pageid || page.pageid;

        item.to_uin = parseInt(nd.attr.touin, 10) || 0;

        item.uin = parseInt(nd.attr.uin, 10) || 0;

        // 支持page级别的busi_info
        item.busi_info = nd.attr.busi || "";

        debug(
            "node path: %o",
            nodes.map(n => {
                return {
                    ...n,
                    el: n.el && n.el.tagName
                };
            })
        );

        // 页面级别行为
        if (nodes.length === 1) {
            if (nd.attr.reporttype) {
                item.reportType = parseInt(
                    nd.attr.reporttype || "1",
                    10
                ) as ENUM_USER_ACTION_REPORT_TYPE;
            }

            level = ACTION_LEVEL.PAGE;

            // 模块级别行为
        } else if (nodes.length === 2) {
            const mdnd: PathNode = nodes[1];

            item.module_id = mdnd.attr.mdid;

            item.to_uin = parseInt(mdnd.attr.touin, 10) || item.to_uin;

            // busi_info支持从page上继承
            item.busi_info = EventCompose.mergeBusiInfo(
                item.busi_info,
                mdnd.attr.busi || ""
            );

            item.reportType =
                parseInt(mdnd.attr.reporttype || nd.attr.reporttype, 10) ||
                item.reportType;

            level = ACTION_LEVEL.MODULE;

            // 子模块级别行为
        } else if (nodes.length === 3) {
            const mdnd: PathNode = nodes[1];
            const sbmdnd: PathNode = nodes[2];

            item.module_id = mdnd.attr.mdid;
            item.sub_module_id = sbmdnd.attr.sbmdid;

            // busi_info支持从page上继承
            item.busi_info = EventCompose.mergeBusiInfo(
                item.busi_info,
                sbmdnd.attr.busi || ""
            );

            item.to_uin =
                parseInt(sbmdnd.attr.touin || mdnd.attr.touin, 10) ||
                item.to_uin;

            item.reportType =
                parseInt(
                    sbmdnd.attr.reporttype ||
                        mdnd.attr.reporttype ||
                        nd.attr.reporttype,
                    10
                ) || item.reportType;

            level = ACTION_LEVEL.SUBMODULE;

            // item级别行为
        } else if (nodes.length === 4) {
            const mdnd: PathNode = nodes[1];
            const sbmdnd: PathNode = nodes[2];
            const itemnd: PathNode = nodes[3];

            item.module_id = mdnd.attr.mdid;
            item.sub_module_id = sbmdnd.attr.sbmdid;
            item.item_id = itemnd.attr.itemid;
            item.item_type = itemnd.attr.itemtype;
            item.position_id = itemnd.attr.position || "";

            // trigger_info支持从module -> submodule -> item的继承
            item.trigger_info =
                itemnd.attr.trigger ||
                sbmdnd.attr.trigger ||
                mdnd.attr.trigger ||
                "";

            // busi_info支持从page上继承
            item.busi_info = EventCompose.mergeBusiInfo(
                item.busi_info,
                itemnd.attr.busi || ""
            );

            // touin支持自上而下继承，page -> module -> submodule -> item
            item.to_uin =
                parseInt(
                    itemnd.attr.touin || sbmdnd.attr.touin || mdnd.attr.touin,
                    10
                ) || item.to_uin;

            item.reportType =
                parseInt(
                    itemnd.attr.reporttype ||
                        sbmdnd.attr.reporttype ||
                        mdnd.attr.reporttype ||
                        nd.attr.reporttype,
                    10
                ) || item.reportType;

            // item可以自定义点击行为的action_id
            item.action_id = parseInt(itemnd.attr.actionid, 10) || 0;

            level = ACTION_LEVEL.ITEM;

            const rule = itemnd.attr.itemrule;

            if (typeof rule === "string") {
                try {
                    const ro = JSON.parse(rule);

                    // json内容不一定是按照协议类型填写的，转义一下
                    item.test_id = parseInt(ro.test_id, 10) || 0;
                    item.rule_id = parseInt(ro.rule_id, 10) || 0;
                    item.str_ruleid =
                        typeof ro.str_ruleid === "string"
                            ? ro.str_ruleid
                            : JSON.stringify(ro.str_ruleid) || "";
                } catch (err) {}
            }
        }

        return {
            level,
            page,
            item
        };
    }
}
